import clsx from 'clsx';
import { lazy, useEffect, useMemo } from 'react';

import { useAppSelector } from '@/hooks/useAppSelector.ts';
import { useLocale } from '@/hooks/useLocale.ts';
import { useSettings } from '@/hooks/useSettings.ts';
import { useSoapboxConfig } from '@/hooks/useSoapboxConfig.ts';
import { useTheme } from '@/hooks/useTheme.ts';
import { normalizeSoapboxConfig } from '@/normalizers/index.ts';
import { startSentry } from '@/sentry.ts';
import { getThemePreset, presetToSoapboxConfig, saveStoredTheme } from '@/utils/theme-presets.ts';
import { generateThemeCss } from '@/utils/theme.ts';

const Helmet = lazy(() => import('@/components/helmet.tsx'));

interface ISoapboxHead {
  children: React.ReactNode;
}

/** Injects metadata into site head with Helmet. */
const SoapboxHead: React.FC<ISoapboxHead> = ({ children }) => {
  const { locale, direction } = useLocale();
  const { demo, reduceMotion, underlineLinks, demetricator, themePreset } = useSettings();
  const soapboxConfig = useSoapboxConfig();
  const theme = useTheme();

  const colorConfig = useMemo(() => {
    if (demo) return normalizeSoapboxConfig({ brandColor: '#0482d8' });
    const preset = getThemePreset(themePreset);
    return preset ? presetToSoapboxConfig(preset) : soapboxConfig;
  }, [demo, themePreset, soapboxConfig]);

  const themeCss = generateThemeCss(colorConfig);
  const dsn = soapboxConfig.sentryDsn;

  const bodyClass = clsx('h-full bg-white text-base black:bg-black dark:bg-primary-900', {
    'no-reduce-motion': !reduceMotion,
    'underline-links': underlineLinks,
    'demetricator': demetricator,
    'font-sans': true,
    '!font-arabic': ['ar', 'fa'].includes(locale),
    '!font-javanese': locale === 'jv',
  });

  // Only the user's own choices are stored, not defaults from the server.
  const storedThemeMode = useAppSelector((state) => state.settings.themeMode);
  const storedThemePreset = useAppSelector((state) => state.settings.themePreset);

  useEffect(() => {
    saveStoredTheme({
      themeMode: typeof storedThemeMode === 'string' ? storedThemeMode : undefined,
      themePreset: typeof storedThemePreset === 'string' ? storedThemePreset : null,
    });
  }, [storedThemeMode, storedThemePreset]);

  useEffect(() => {
    if (dsn) {
      startSentry(dsn).catch(console.error);
    }
  }, [dsn]);

  return (
    <>
      <Helmet>
        <html lang={locale} className={clsx('h-full', { 'dark': theme === 'dark', 'dark black': theme === 'black' })} />
        <body className={bodyClass} dir={direction} />
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {themeCss && <style id='theme' type='text/css'>{`:root{${themeCss}}`}</style>}
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {['dark', 'black'].includes(theme) && <style type='text/css'>{':root { color-scheme: dark; }'}</style>}
        <meta name='theme-color' content={colorConfig.brandColor} />
      </Helmet>

      {children}
    </>
  );
};

export default SoapboxHead;
